import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { Edition } from '../../common/editions';
import { ApplicationRoutes } from '../../common/routes';
import { AppConfigService } from '../../core/app-config.service';
import { CustomerVoiceRouterService } from '../../core/cv-router.service';
import { Customer } from '../../customers/customer-common';
import { CustomerSelectionService } from '../../customers/customer-selection.service';
import { CustomerService } from '../../customers/customers.service';
import { ExpressCtaDialogService } from '../express-cta-dialog/express-cta-dialog.service';
import { SendSingleRequestDialogComponent } from '../send-single-request-dialog/send-single-request-dialog.component';

@Component({
  selector: 'app-floating-review-request-button',
  templateUrl: './floating-send-review-request-button.component.html',
  styleUrls: ['./floating-send-review-request-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FloatingSendReviewRequestButtonComponent implements OnInit {
  private sendSingleRequestDialogRef: MatDialogRef<SendSingleRequestDialogComponent>;
  clickTrackingLabel = 'floating-action-button';

  showFloatingActionButton: boolean;
  isCustomersPage$: Observable<boolean>;
  toggleButton$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private appConfigService: AppConfigService,
    private sendSingleRequestDialog: MatDialog,
    private customerService: CustomerService,
    private customerVoiceRouter: CustomerVoiceRouterService,
    private customerSelector: CustomerSelectionService,
    private router: Router,
    private ctaDialogService: ExpressCtaDialogService,
    private snowplowService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    const sendReviewRequestLabel = '/customers/send-review-request';
    const editEmailTemplateLabel = '/templates/email/';
    this.showFloatingActionButton =
      !this.router.url.includes(sendReviewRequestLabel) && !this.router.url.includes(editEmailTemplateLabel);

    this.isCustomersPage$ = this.customerVoiceRouter.getRouteUrl(ApplicationRoutes.customers).pipe(
      take(1),
      map((customerTable) => this.router.isActive(customerTable, false)),
    );
  }

  handleClick(): void {
    combineLatest([this.appConfigService.appConfig, this.isCustomersPage$])
      .pipe(take(1))
      .subscribe(([appConfig, isCustomersPage]) => {
        if (isCustomersPage) {
          if (this.customerSelector.nbSelected() > 1 && appConfig.edition === Edition.Express) {
            this.openBulkExpressCta('bulk-send-button-express-cta');
          } else if (this.customerSelector.nbSelected() !== 0) {
            this.navigateToSendBulkPage();
          }
        } else {
          this.openSendRequest();
        }
      });
  }

  navigateToSendBulkPage(): void {
    this.customerVoiceRouter.navigateByUrl(ApplicationRoutes.sendReviewRequest);
  }

  get customersSelected(): number {
    return this.customerSelector.nbSelected();
  }

  get toggleButton$(): Observable<boolean> {
    return this.toggleButton$$.asObservable();
  }

  openSendRequest(): void {
    this.toggleButton$$.next(false);
    this.sendSingleRequestDialogRef = this.sendSingleRequestDialog.open(SendSingleRequestDialogComponent);
    this.sendSingleRequestDialogRef
      .afterClosed()
      .pipe(
        tap(() => this.toggleButton$$.next(true)),
        filter((result) => {
          return !!result;
        }),
      )
      .subscribe((customer: Customer) => this.customerService.prependNewCustomer(customer));
  }

  openBulkExpressCta(eventLabel: string): void {
    this.ctaDialogService.open(
      'https://vstatic-prod.apigateway.co/customer-voice-client.9223372035066326780.prod/assets/images/express_cta/bulk.png',
      'EXPRESS_CTA_DIALOG.IMAGE_TEXT.BULK_REVIEW_REQUESTS',
      `${eventLabel}-upgraded`,
    );
  }
}
